import Text from "components/atoms/Text";
import { COLOR_SYSTEM, Flex, ToggleButton } from "components/atoms";
import styled from "styled-components";
import { DateRangePicker, DateKind } from "components/molecules/DateRangePicker";
import SubscriptionPaymentCard from "./SubscriptionPaymentCard";
import PointsTransactionList from "./PointsTransactionList";
import { getDateStringFromToday } from "utils/TimeUtil";
import {
  OrderInfo,
  SubscriptionPayment,
  PointsTransaction,
} from "common/models/types";

interface Props {
  transactions: PointsTransaction[];
  subscriptionPayments: SubscriptionPayment[];
  orders: OrderInfo[];
  startAt: string;
  endAt: string;
  setStartAt: (startAt: string) => void;
  setEndAt: (endAt: string) => void;
}

type PaymentListItem =
  | { kind: "point"; createdAtMs: number; data: OrderInfo }
  | { kind: "subscription"; createdAtMs: number; data: SubscriptionPayment };

const toEpochMs = (raw: Date | string | undefined | null): number => {
  if (!raw) return 0;
  const ts = raw instanceof Date ? raw.getTime() : new Date(raw).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

export const mergePaymentItems = (
  orders: OrderInfo[],
  subPayments: SubscriptionPayment[]
): PaymentListItem[] =>
  [
    ...orders.map<PaymentListItem>((o) => ({
      kind: "point",
      createdAtMs: toEpochMs(o.paidAt || o.createdAt),
      data: o,
    })),
    ...subPayments.map<PaymentListItem>((p) => ({
      kind: "subscription",
      createdAtMs: toEpochMs(p.paidAt || p.reservedAt || p.createdAt),
      data: p,
    })),
  ].sort((a, b) => b.createdAtMs - a.createdAtMs);

const Container = styled.div`
  width: 100%;
`;

const TwoCol = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
`;
const Col = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ColTitle = styled(Text)`
  display: block;
  margin-bottom: 8px;
`;
const ColEmpty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")[400]};
  font-size: 14px;
`;

const Orders: React.FC<Props> = ({
  transactions,
  subscriptionPayments,
  orders,
  startAt,
  endAt,
  setStartAt,
  setEndAt,
}) => {
  return (
    <Container>
      <Flex alignItems="center" customStyle={{ marginBottom: "20px" }}>
        <Flex alignItems="center" customStyle={{}} gap="12px">
          <ToggleButton
            color={"Gray"}
            size={"Medium"}
            variant={"Outlined"}
            onClick={() => {
              setStartAt(getDateStringFromToday());
              setEndAt(getDateStringFromToday());
            }}
          >
            오늘
          </ToggleButton>
          <ToggleButton
            color={"Gray"}
            size={"Medium"}
            variant={"Outlined"}
            onClick={() => {
              setStartAt(getDateStringFromToday({ days: -7 }));
              setEndAt(getDateStringFromToday());
            }}
          >
            1주일
          </ToggleButton>
          <ToggleButton
            color={"Gray"}
            size={"Medium"}
            variant={"Outlined"}
            onClick={() => {
              setStartAt(getDateStringFromToday({ months: -1 }));
              setEndAt(getDateStringFromToday());
            }}
          >
            1개월
          </ToggleButton>
          <ToggleButton
            color={"Gray"}
            size={"Medium"}
            variant={"Outlined"}
            onClick={() => {
              setStartAt(getDateStringFromToday({ months: -3 }));
              setEndAt(getDateStringFromToday());
            }}
          >
            3개월
          </ToggleButton>
          <DateRangePicker
            startDate={startAt}
            endDate={endAt}
            disabled={false}
            onChangeDate={(name: DateKind, date: Date) => {
              switch (name) {
                case "start":
                  setStartAt(date.toISOString().substring(0, 10));
                  break;
                case "end":
                  setEndAt(date.toISOString().substring(0, 10));
                  break;
              }
            }}
          />
        </Flex>
      </Flex>
      <TwoCol>
        <Col>
          <ColTitle textType="Body1Bold" color={COLOR_SYSTEM.get("Gray")[800]}>
            포인트 거래내역
          </ColTitle>
          <PointsTransactionList transactions={transactions} orders={orders} />
        </Col>
        <Col>
          <ColTitle textType="Body1Bold" color={COLOR_SYSTEM.get("Gray")[800]}>
            구독 결제
          </ColTitle>
          {subscriptionPayments.length ? (
            subscriptionPayments.map((p) => (
              <SubscriptionPaymentCard key={`sub-${p.id}`} payment={p} />
            ))
          ) : (
            <ColEmpty>구독 결제 내역이 없습니다.</ColEmpty>
          )}
        </Col>
      </TwoCol>
    </Container>
  );
};

export default Orders;
